/* Comments, with replies. One component for the feed AND Prayer Requests.
 *
 * WHY ONE. The two were separate files with the same shape — a list, a
 * composer, a spinner — and every change had to be made twice or drift. They
 * differ in exactly two ways: where the comments come from, and whether a
 * comment can carry a help_kind badge. Both are props.
 *
 * The API keeps its two endpoints, and should: a prayer comment carries
 * help_kind and a post comment has no use for it. What is shared is the
 * rendering, which is where the duplication actually hurt.
 *
 * REPLIES ARE ONE LEVEL. A comment can have replies; a reply cannot. Replying
 * to a reply attaches to the same parent and prefixes the name, which is what
 * every social product settles on — the fourth level of a tree has no room on
 * a phone. The server and a database trigger both enforce it.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { CornerDownRight, Loader2, SendHorizontal } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { EmojiButton, insertAtCursor } from '@/components/ui/emoji-picker'
import { useEditComment, useMe, useToggleCommentLike } from '@/api/queries'
import { cn, timeAgo } from '@/lib/utils'

/** The shape both comment kinds share. */
export interface ThreadComment {
  id: string
  body: string
  parent_id: string | null
  created_at: string
  author: { id: string; name: string; avatar_url: string | null }
  /** Set once somebody changed it — shown beside the time. */
  edited_at?: string | null
  like_count?: number
  liked_by_viewer?: boolean
  /** Prayer comments only — an offer of help rather than an ordinary remark. */
  help_kind?: string | null
}

export interface CommentThreadProps {
  comments: ThreadComment[]
  isLoading: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
  /** Post it. `parentId` is null for a top-level comment. */
  onSend: (body: string, parentId: string | null) => void
  isSending: boolean
  /** Rendered beside the author's name — the help badge on a prayer comment. */
  renderBadge?: (comment: ThreadComment) => ReactNode
  placeholder?: string
  /** A comment to scroll to and mark, arriving from a "replied to you" tap. */
  focusId?: string | null
}

export function CommentThread({
  comments,
  isLoading,
  hasMore,
  onLoadMore,
  isLoadingMore,
  onSend,
  isSending,
  renderBadge,
  placeholder = 'Write a comment…',
  focusId,
}: CommentThreadProps) {
  const [draft, setDraft] = useState('')
  /* Which comment the composer is answering. Null means a new top-level one. */
  const [replyingTo, setReplyingTo] = useState<ThreadComment | null>(null)
  const { data: me } = useMe()

  /* GROUPED IN THE CLIENT. A thread is small and already loaded, so a second
     query per comment to fetch its replies would be absurd. */
  const tops = comments.filter((c) => !c.parent_id)
  const repliesOf = (id: string) =>
    comments.filter((c) => c.parent_id === id)

  /* SCROLL TO THE COMMENT THAT WAS REPLIED TO.
     Arriving from the bell used to drop the reader at the top of the thread to
     hunt for the line that concerned them. Runs once the comment is actually in
     the list — the first render is a spinner, so a ref alone would find
     nothing. The highlight fades on its own; a permanent one would still be
     there tomorrow with no way to dismiss it. */
  const focusRef = useRef<HTMLLIElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [lit, setLit] = useState(false)
  const landed = focusId ? comments.some((c) => c.id === focusId) : false

  useEffect(() => {
    if (!landed || !focusRef.current) return
    focusRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setLit(true)
    const t = setTimeout(() => setLit(false), 2600)
    return () => clearTimeout(t)
  }, [landed, focusId])

  function submit(e: FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body || isSending) return
    const parent = replyingTo?.id ?? null
    // Cleared immediately rather than in onSuccess: somebody typing a second
    // comment should not have the first reappear under their cursor.
    setDraft('')
    setReplyingTo(null)
    onSend(body, parent)
  }

  return (
    <div className="border-t border-line-2 px-s4 py-s3">
      {isLoading ? (
        <div className="flex justify-center py-s3">
          <Loader2
            className="h-4 w-4 animate-spin text-ink-3"
            aria-label="Loading comments"
          />
        </div>
      ) : null}

      <ul className="space-y-s3">
        {tops.map((c) => (
          <li
            key={c.id}
            ref={c.id === focusId ? focusRef : undefined}
            className={
              lit && c.id === focusId
                ? 'rounded-lg bg-brand-weak/60 ring-2 ring-brand transition-colors duration-700'
                : 'rounded-lg transition-colors duration-700'
            }
          >
            <CommentRow
              comment={c}
              renderBadge={renderBadge}
              onReply={() => setReplyingTo(c)}
            />

            {/* Replies, indented once and once only. */}
            {repliesOf(c.id).length ? (
              <ul className="mt-s2 space-y-s2 pl-s6">
                {repliesOf(c.id).map((r) => (
                  <li key={r.id}>
                    <CommentRow
                      comment={r}
                      renderBadge={renderBadge}
                      // Replying to a reply answers its PARENT — that is what
                      // keeps the thread one level deep.
                      onReply={() => setReplyingTo(c)}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>

      {hasMore ? (
        <button
          onClick={onLoadMore}
          disabled={isLoadingMore}
          className="mt-s2 text-sm font-semibold text-brand hover:underline"
        >
          {isLoadingMore ? 'Loading…' : 'See earlier comments'}
        </button>
      ) : null}

      {/* Who you are answering, and a way out of it. Without this the reply
          goes somewhere the writer cannot see. */}
      {replyingTo ? (
        <div className="mt-s3 flex items-center gap-s2 rounded bg-surface-2 px-s3 py-s2 text-sm">
          <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-ink-2" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-ink-2">
            Replying to <strong className="text-ink">{replyingTo.author.name}</strong>
          </span>
          <button
            type="button"
            onClick={() => setReplyingTo(null)}
            className="shrink-0 font-semibold text-brand hover:underline"
          >
            Cancel
          </button>
        </div>
      ) : null}

      <form onSubmit={submit} className="mt-s3 flex items-end gap-s2">
        <Avatar name={me?.first_name ?? ''} src={me?.avatar_url} size="sm" />
        {/* The emoji button sits INSIDE the field, on a row under the text —
            where Facebook puts it, and where the avatar, GIF and sticker
            buttons have room to join it later. */}
        <div className="min-w-0 flex-1 rounded-lg border border-line-2 bg-surface-2 px-s3 py-s2 focus-within:shadow-focus">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={replyingTo ? `Reply to ${replyingTo.author.name}…` : placeholder}
            aria-label={replyingTo ? 'Your reply' : 'Your comment'}
            className="w-full bg-transparent text-base outline-none"
          />
          <div className="mt-1 flex items-center">
            <EmojiButton
              label={replyingTo ? 'Emoji for your reply' : 'Emoji for your comment'}
              onPick={(emoji) => {
                const { text, caret } = insertAtCursor(
                  inputRef.current,
                  draft,
                  emoji,
                )
                setDraft(text)
                // Focus and caret restored on the next frame, after React has
                // written the new value — otherwise the caret jumps to the end.
                requestAnimationFrame(() => {
                  inputRef.current?.focus()
                  inputRef.current?.setSelectionRange(caret, caret)
                })
              }}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={!draft.trim() || isSending}
          aria-label={replyingTo ? 'Post reply' : 'Post comment'}
          className="shrink-0 rounded-pill p-s2 text-brand disabled:text-ink-3"
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          ) : (
            <SendHorizontal className="h-5 w-5" aria-hidden />
          )}
        </button>
      </form>
    </div>
  )
}

function CommentRow({
  comment,
  renderBadge,
  onReply,
}: {
  comment: ThreadComment
  renderBadge?: (c: ThreadComment) => ReactNode
  onReply: () => void
}) {
  const { data: me } = useMe()
  const like = useToggleCommentLike()
  const editComment = useEditComment()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.body)

  const isAuthor = me?.id === comment.author.id
  const likes = comment.like_count ?? 0

  function saveEdit() {
    const body = draft.trim()
    // An unchanged Save is a pointless round trip AND a pointless "edited"
    // mark — and an "edited" meaning "somebody opened this and closed it
    // again" is worse than no mark at all.
    if (!body || body === comment.body || editComment.isPending) {
      setEditing(false)
      return
    }
    editComment.mutate(
      { commentId: comment.id, body },
      { onSuccess: () => setEditing(false) },
    )
  }

  return (
    <div className="flex gap-s2">
      <Avatar
        name={comment.author.name}
        src={comment.author.avatar_url}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        {editing ? (
          /* Inline, not a dialog. A comment is one line being corrected;
             taking over the screen for it loses the thread it belongs to,
             which is most of what tells you whether the correction reads. */
          <div className="rounded-lg border border-line-2 bg-surface-2 px-s3 py-s2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 5000))}
              aria-label="Edit your comment"
              rows={2}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setDraft(comment.body)
                  setEditing(false)
                }
                // Enter saves, Shift+Enter breaks the line — what a one-line
                // correction wants, and what the composer above already does.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  saveEdit()
                }
              }}
              className="w-full resize-none bg-transparent text-base outline-none"
            />
            <div className="mt-1 flex items-center gap-s3 text-xs">
              <button
                type="button"
                onClick={saveEdit}
                disabled={!draft.trim() || editComment.isPending}
                className="font-semibold text-brand disabled:text-ink-3"
              >
                {editComment.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(comment.body)
                  setEditing(false)
                }}
                className="font-semibold text-ink-2 hover:text-ink"
              >
                Cancel
              </button>
              {editComment.isError ? (
                <span className="font-semibold text-danger">
                  {(editComment.error as Error)?.message ?? 'That did not save.'}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="inline-block max-w-full rounded-lg bg-surface-2 px-s3 py-s2">
            <div className="flex flex-wrap items-center gap-s2">
              <span className="text-sm font-semibold">{comment.author.name}</span>
              {renderBadge?.(comment)}
            </div>
            <p className="whitespace-pre-wrap break-words text-base">
              {comment.body}
            </p>
          </div>
        )}

        {/* Like · N | Reply, the row every threaded feed settles on. The count
            rides ON the Like rather than beside it, so a comment nobody has
            liked shows one word instead of a lonely zero. */}
        <div className="mt-0.5 flex items-center gap-s3 pl-s3 text-xs text-ink-3">
          <span>{timeAgo(comment.created_at)}</span>
          {comment.edited_at ? (
            <span title="This comment was edited">edited</span>
          ) : null}
          <button
            type="button"
            onClick={() => like.mutate(comment.id)}
            aria-pressed={comment.liked_by_viewer ?? false}
            className={cn(
              'font-semibold hover:text-brand',
              comment.liked_by_viewer ? 'text-brand' : 'text-ink-2',
            )}
          >
            Like{likes > 0 ? ` · ${likes}` : ''}
          </button>
          <button
            type="button"
            onClick={onReply}
            className="font-semibold text-ink-2 hover:text-brand"
          >
            Reply
          </button>
          {isAuthor && !editing ? (
            <button
              type="button"
              onClick={() => {
                setDraft(comment.body)
                setEditing(true)
              }}
              className="font-semibold text-ink-2 hover:text-brand"
            >
              Edit
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
